/**
 * Custom Card Portal — client-side portal logic
 *
 * Upload to theme as: assets/gd-portal.js
 *
 * Responsibilities:
 *   - Access gate: SHA-256 passphrase verification + 8-hour session cookie
 *   - Poll /cart.js to update selections counter
 *   - Wire up "Review selections" modal (step 1: review, step 2: form, step 3: done)
 *   - Filter product grid by search and/or category tag
 *   - Validate and submit selection (action + field names come from form data attributes,
 *     rendered server-side by Liquid — not exposed in JS config)
 *   - Show confirmation screen, clear cart, reload
 *
 * Metadata (clientName, repLabel) from window.PORTAL_CONFIG set by the Liquid template.
 * Form endpoint and field names are read from data-* attributes on #portal-form.
 */

// ── ACCESS GATE ──────────────────────────────────────────────────────────────
// Self-contained IIFE. Reads the SHA-256 hash from <meta name="x-pauth">.
// The raw passphrase is never sent anywhere — only hashed client-side via Web Crypto.
// If the metafield is not set, the meta tag is absent and this block exits immediately.
(function () {
  'use strict';

  var meta = document.querySelector('meta[name="x-pauth"]');
  if (!meta || !meta.content) return;                          // gate not configured
  if (!document.documentElement.classList.contains('portal--locked')) return; // cookie valid

  var expectedHash = meta.content;
  var gate         = document.getElementById('portal-gate');
  var input        = document.getElementById('gate-input');
  var submitBtn    = document.getElementById('gate-submit');
  var errorEl      = document.getElementById('gate-error');

  if (!gate || !input || !submitBtn) return;

  // Focus on load
  setTimeout(function () { input.focus(); }, 50);

  function sha256hex(str) {
    var data = new TextEncoder().encode(str);
    return crypto.subtle.digest('SHA-256', data).then(function (buf) {
      return Array.from(new Uint8Array(buf))
        .map(function (b) { return b.toString(16).padStart(2, '0'); })
        .join('');
    });
  }

  function setGateError(msg) {
    if (!errorEl) return;
    errorEl.textContent = msg;
    errorEl.hidden = !msg;
  }

  // Rate-limiting: lock out after MAX_ATTEMPTS failed tries, with cooldown
  var MAX_ATTEMPTS  = 5;
  var COOLDOWN_SECS = 30;
  var failCount     = 0;
  var lockedUntil   = 0;

  function startCooldown() {
    submitBtn.disabled = true;
    input.disabled     = true;

    function tick() {
      var remaining = Math.ceil((lockedUntil - Date.now()) / 1000);
      if (remaining <= 0) {
        failCount       = 0;
        input.disabled  = false;
        submitBtn.disabled = false;
        submitBtn.textContent = 'Continue →';
        setGateError('');
        input.focus();
        return;
      }
      setGateError('Too many attempts. Please wait ' + remaining + ' second' + (remaining === 1 ? '' : 's') + '.');
      submitBtn.textContent = 'Locked (' + remaining + 's)';
      setTimeout(tick, 1000);
    }
    tick();
  }

  function unlock() {
    var expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
    // Secure flag ensures cookie is only sent over HTTPS
    document.cookie = 'ccp_auth=1; expires=' + expires + '; path=/; SameSite=Strict; Secure';
    document.documentElement.classList.remove('portal--locked');
    document.documentElement.classList.add('portal--unlocking'); // triggers fade-in animation on content
    gate.classList.add('gate--exit');
    setTimeout(function () { gate.hidden = true; }, 350);
  }

  function attempt() {
    // Respect active cooldown
    if (Date.now() < lockedUntil) return;

    var val = (input.value || '').trim();
    if (!val) {
      setGateError('Please enter your access code.');
      input.focus();
      return;
    }
    setGateError('');
    submitBtn.disabled = true;
    submitBtn.textContent = 'Checking…';

    sha256hex(val).then(function (hash) {
      if (hash === expectedHash) {
        unlock();
      } else {
        failCount++;
        input.value = '';
        if (failCount >= MAX_ATTEMPTS) {
          lockedUntil = Date.now() + COOLDOWN_SECS * 1000;
          startCooldown();
        } else {
          var remaining = MAX_ATTEMPTS - failCount;
          setGateError('Incorrect access code. ' + remaining + ' attempt' + (remaining === 1 ? '' : 's') + ' remaining.');
          input.focus();
          submitBtn.disabled = false;
          submitBtn.textContent = 'Continue →';
        }
      }
    }).catch(function () {
      setGateError('An error occurred. Please try again.');
      submitBtn.disabled = false;
      submitBtn.textContent = 'Continue →';
    });
  }

  // ── Auto-unlock via authorized link (?token=...) ─────────────────────────
  // G+D can share gdcardskins.customcardportal.com?token=<raw-passphrase>
  // The token is hashed client-side and compared to the stored hash — same as
  // the manual password flow. On match: unlock + strip token from URL so the
  // browser shows the clean domain. On mismatch: gate stays visible, no error
  // shown (avoids leaking that a token was attempted).
  (function () {
    var params = new URLSearchParams(location.search);
    var urlToken = params.get('token');
    if (!urlToken) return;
    history.replaceState(null, '', location.pathname + location.hash);
    sha256hex(urlToken).then(function (hash) {
      if (hash === expectedHash) unlock();
    });
  })();

  submitBtn.addEventListener('click', attempt);
  input.addEventListener('keydown', function (e) {
    if (e.key === 'Enter') attempt();
    if (e.key !== 'Enter') setGateError(''); // clear error on any key
  });

})();

// ── PORTAL LOGIC ─────────────────────────────────────────────────────────────

(function () {
  'use strict';

  const C = window.PORTAL_CONFIG;
  if (!C) {
    console.error('[portal] PORTAL_CONFIG not set; template did not render correctly');
    return;
  }

  // ---- URL clean-up -------------------------------------------------------
  // Must happen BEFORE replaceState strips the query string.

  // 1. Check for ?added=1 (Customify → /cart → subdomain router → portal?added=1)
  const _params = new URLSearchParams(location.search);
  const _justAdded = _params.has('added');

  // 2. Rewrite URL bar to show clean root (hides /pages/gdskins-portal path)
  if (location.pathname !== '/') {
    history.replaceState({}, '', '/');
  }

  // ---- DOM refs -----------------------------------------------------------

  const $ = (id) => document.getElementById(id);
  const el = {
    count:      $('portal-count'),
    review:     $('portal-review'),
    search:     $('portal-search'),
    grid:       $('portal-grid'),
    modal:      $('portal-modal'),
    close:      $('portal-modal-close'),
    cancel:     $('portal-modal-cancel'),    // "Cancel" in review step
    continuebtn: $('portal-review-continue'), // "Continue →" in review step
    formback:   $('portal-form-back'),        // "← Back" in form step
    preview:    $('portal-selections-preview'),
    form:       $('portal-form'),
    done:       $('portal-done'),
    doneSum:    $('portal-done-summary'),
    stepReview: document.querySelector('.modal__step[data-step="review"]'),
    stepForm:   document.querySelector('.modal__step[data-step="form"]'),
    stepDone:   document.querySelector('.modal__step[data-step="done"]'),
  };

  // Form inputs — reference via elements[] to avoid `form.name` IDL conflict
  const fi = {
    name:    el.form ? el.form.elements['name']    : null,
    company: el.form ? el.form.elements['company'] : null,
    rep:     el.form ? el.form.elements['rep']     : null,
    email:   el.form ? el.form.elements['email']   : null,
    notes:   el.form ? el.form.elements['notes']   : null,
  };

  // Form endpoint + field names are rendered server-side into data attributes.
  // They are intentionally not part of window.PORTAL_CONFIG.
  const FD = el.form ? {
    action:  el.form.dataset.action  || '',
    name:    el.form.dataset.fName    || '',
    company: el.form.dataset.fCompany || '',
    rep:     el.form.dataset.fRep     || '',
    email:   el.form.dataset.fEmail   || '',
    designs: el.form.dataset.fDesigns || '',
    notes:   el.form.dataset.fNotes   || '',
  } : {};

  // ---- cart polling -------------------------------------------------------

  let lastCart = null;

  async function pollCart() {
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin' })
        .then((r) => r.json());
      lastCart = cart;
      el.count.textContent = String(cart.item_count);
      el.review.disabled = cart.item_count === 0;
    } catch (e) {
      console.warn('[portal] cart poll failed', e);
    }
  }

  // Poll on focus/visibility so users who navigate away and come back see updates
  pollCart();
  setInterval(pollCart, 10000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') pollCart();
  });

  // ---- post-add toast -----------------------------------------------------
  // Fires when subdomain router returns user to portal with ?added=1
  // (captured before replaceState wiped the query string above)

  if (_justAdded) {
    showToast('Design added — review or add more, then click "Review selections"');
  }

  // ---- toast helper -------------------------------------------------------

  function showToast(msg) {
    const t = document.createElement('div');
    t.className = 'portal__toast';
    t.textContent = msg;
    document.body.appendChild(t);
    requestAnimationFrame(() => t.classList.add('portal__toast--visible'));
    setTimeout(() => {
      t.classList.remove('portal__toast--visible');
      setTimeout(() => t.remove(), 300);
    }, 3500);
  }

  // ---- unified filter (search + category tag) + pagination -------------------

  let activeTag = '';

  function applyFilters() {
    const q = el.search?.value.trim().toLowerCase() || '';
    el.grid?.querySelectorAll('.card').forEach((card) => {
      let match = true;
      // Tag filter
      if (activeTag) {
        const tags = (card.dataset.tags || '').split(',').map((t) => t.trim());
        if (!tags.includes(activeTag)) match = false;
      }
      // Search filter
      if (match && q) {
        const title = card.querySelector('.card__title')?.textContent?.toLowerCase() || '';
        if (!title.includes(q)) match = false;
      }
      card.hidden = !match;
    });
  }

  el.search?.addEventListener('input', () => { applyFilters(); });

  // ---- category filter bar ------------------------------------------------
  // Reads data-tags from each card (set by Liquid), builds filter chips.
  // Tags are lowercase, comma-separated. System tag 'direct-add' is excluded.

  function buildFilters() {
    const bar = $('portal-filter-bar');
    if (!bar || !el.grid) return;

    const EXCLUDE = new Set([
      'direct-add',
      'custom', 'customify', 'customizable', 'customize',
      'custom apple card', 'customize apple card',
    ]);
    const tagSet = new Set();
    el.grid.querySelectorAll('.card[data-tags]').forEach((card) => {
      const raw = card.dataset.tags || '';
      raw.split(',')
        .map((t) => t.trim())
        .filter((t) => t && !EXCLUDE.has(t))
        .forEach((t) => tagSet.add(t));
    });

    if (tagSet.size === 0) return; // nothing to filter by

    bar.hidden = false;

    function makeChip(label, tag) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'filter-chip' + (tag === '' ? ' filter-chip--active' : '');
      btn.dataset.tag = tag;
      btn.textContent = label;
      return btn;
    }

    bar.appendChild(makeChip('All', ''));
    Array.from(tagSet)
      .sort()
      .forEach((tag) => {
        const label = tag.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        bar.appendChild(makeChip(label, tag));
      });

    bar.addEventListener('click', (e) => {
      const chip = e.target.closest('.filter-chip');
      if (!chip) return;
      bar.querySelectorAll('.filter-chip').forEach((c) => c.classList.remove('filter-chip--active'));
      chip.classList.add('filter-chip--active');
      activeTag = chip.dataset.tag;
      applyFilters();
    });
  }

  buildFilters();
  applyFilters();

  // ---- pre-made design: variant selection + direct cart add ---------------
  // Products tagged 'direct-add' bypass Customify entirely.
  // When a dropdown changes, match the selected options against the variants JSON
  // and update the button's data-variant-id.

  function resolveVariant(card) {
    const jsonEl = card.querySelector('.card__variants-json');
    if (!jsonEl) return null;
    let variants;
    try { variants = JSON.parse(jsonEl.textContent); } catch { return null; }

    const selects = Array.from(card.querySelectorAll('.card__option-select'));
    const chosen = selects.map(s => s.value);

    return variants.find(v =>
      v.available &&
      (chosen.length < 1 || !v.opt1 || v.opt1 === chosen[0]) &&
      (chosen.length < 2 || !v.opt2 || v.opt2 === chosen[1]) &&
      (chosen.length < 3 || !v.opt3 || v.opt3 === chosen[2])
    ) || variants.find(v =>
      // fallback: any variant matching selection (even if unavailable)
      (chosen.length < 1 || !v.opt1 || v.opt1 === chosen[0]) &&
      (chosen.length < 2 || !v.opt2 || v.opt2 === chosen[1]) &&
      (chosen.length < 3 || !v.opt3 || v.opt3 === chosen[2])
    ) || null;
  }

  // Update button variant-id and card image when dropdowns change
  el.grid?.addEventListener('change', (e) => {
    const select = e.target.closest('.card__option-select');
    if (!select) return;
    const card = select.closest('.card--premade');
    if (!card) return;
    const variant = resolveVariant(card);
    const btn = card.querySelector('.card__select-btn');
    if (btn) btn.dataset.variantId = variant ? String(variant.id) : '';
    // Swap image + update zoom src to match selected variant
    if (variant && variant.image) {
      const img = card.querySelector('.card__img');
      if (img) {
        img.src = variant.image;
        if (variant.imageFull) img.dataset.srcFull = variant.imageFull;
      }
      const zoomBtn = card.querySelector('.card__zoom-btn');
      if (zoomBtn && variant.imageFull) zoomBtn.dataset.srcFull = variant.imageFull;
    }
  });

  // ---- pre-made card quantity stepper (inc/dec on the card) -----------------

  el.grid?.addEventListener('click', (e) => {
    const dec = e.target.closest('.card__qty-dec');
    const inc = e.target.closest('.card__qty-inc');
    const btn = dec || inc;
    if (!btn) return;
    const card  = btn.closest('.card--premade');
    const input = card?.querySelector('.card__qty-input');
    if (!input) return;
    const current = parseInt(input.value, 10) || 1;
    input.value = dec ? Math.max(1, current - 1) : Math.min(99, current + 1);
  });

  // "Select Design" click → add to cart via AJAX (no page navigation)
  el.grid?.addEventListener('click', async (e) => {
    const btn = e.target.closest('.card__select-btn');
    if (!btn || btn.disabled) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const card     = btn.closest('.card--premade');
    const qtyInput = card?.querySelector('.card__qty-input');
    const quantity = Math.max(1, parseInt(qtyInput?.value || '1', 10));

    btn.disabled = true;
    btn.textContent = 'Adding…';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity }),
      });
      if (!res.ok) throw new Error(`cart/add failed: ${res.status}`);
      await pollCart();
      showToast('Design added — click "Review selections" when ready');
      btn.textContent = '✓ Added';
      setTimeout(() => { btn.textContent = 'Select Design'; btn.disabled = false; }, 2500);
    } catch (err) {
      console.error('[portal] add to cart failed', err);
      btn.textContent = 'Select Design';
      btn.disabled = false;
      showToast('Could not add design — please try again');
    }
  });

  // ---- modal open / close -------------------------------------------------

  el.review?.addEventListener('click', openModal);
  el.close?.addEventListener('click', closeModal);
  el.cancel?.addEventListener('click', closeModal);
  el.continuebtn?.addEventListener('click', () => showStep('form'));
  el.formback?.addEventListener('click', () => showStep('review'));
  el.modal?.addEventListener('click', (e) => {
    if (e.target === el.modal) closeModal();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !el.modal.hidden) closeModal();
  });

  async function openModal() {
    await pollCart();
    if (!lastCart || lastCart.item_count === 0) return;
    renderSelections(lastCart);
    showStep('review');
    el.modal.hidden = false;
    document.body.classList.add('modal-open');
    el.continuebtn?.focus();
  }

  function closeModal() {
    el.modal.hidden = true;
    document.body.classList.remove('modal-open');
    el.form?.reset();
  }

  function showStep(name) {
    el.stepReview.hidden = name !== 'review';
    el.stepForm.hidden   = name !== 'form';
    el.stepDone.hidden   = name !== 'done';
    if (name === 'form') fi.name?.focus();
  }

  // ---- cart line update (shared by stepper and remove) --------------------

  async function updateCartLine(line, quantity) {
    try {
      const changeRes = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ line, quantity }),
      });
      if (!changeRes.ok) throw new Error(`cart/change.js ${changeRes.status}`);

      // Fetch /cart.js for canonical state — change.js response can include
      // qty-0 items in its array, which would corrupt our 1-based line index.
      // /cart.js only returns items that are actually in the cart (qty >= 1).
      const freshCart = await fetch('/cart.js', { credentials: 'same-origin' })
        .then(r => r.json());

      lastCart = freshCart;
      el.count.textContent = String(freshCart.item_count);
      el.review.disabled   = freshCart.item_count === 0;

      if (freshCart.item_count === 0) { closeModal(); return; }
      renderSelections(freshCart);
    } catch (err) {
      console.error('[portal] cart update failed', err);
      showToast('Could not update — please try again');
      // Always re-fetch cart.js after any failure so lastCart stays accurate.
      // A 422 "sold out" error can cause Shopify to silently remove the item,
      // making our cached line indices stale if we skip this fetch.
      try {
        const freshCart = await fetch('/cart.js', { credentials: 'same-origin' })
          .then(r => r.json());
        lastCart = freshCart;
        el.count.textContent = String(freshCart.item_count);
        el.review.disabled   = freshCart.item_count === 0;
        if (freshCart.item_count === 0) { closeModal(); return; }
        renderSelections(freshCart);
      } catch (_) {
        if (lastCart) renderSelections(lastCart);
      }
    }
  }

  // ---- selections rendering -----------------------------------------------

  function renderSelections(cart) {
    // Pass 1-based line index so change.js can target items unambiguously
    const items = cart.items.map((item, i) => formatLineItem(item, i + 1));
    el.preview.innerHTML = items.map(renderSelectionRow).join('');

    // Direct listeners on each button — no delegation, no ambiguity
    el.preview.querySelectorAll('.qty-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const line    = parseInt(btn.dataset.line,  10);
        const delta   = parseInt(btn.dataset.delta, 10);
        const current = parseInt(btn.dataset.qty,   10);
        const newQty  = Math.max(1, current + delta); // floor at 1; use Remove to delete
        if (newQty === current) return;
        updateCartLine(line, newQty);
      });
    });

    el.preview.querySelectorAll('.qty-remove').forEach((btn) => {
      btn.addEventListener('click', () => {
        const line = parseInt(btn.dataset.line, 10);
        if (!line) return;
        updateCartLine(line, 0);
      });
    });
  }

  function formatLineItem(item, line) {
    const p = item.properties || {};

    // Collect ALL Customify-generated file links from cart properties.
    // Customify stores files as URL-valued properties, e.g.:
    //   final-design-1, final-design-2  → print-ready files
    //   design-1, design-2              → design previews
    //   cst-original-image-1            → customer's original uploaded image
    // The exact keys vary — safest approach is to collect every property
    // whose value is an HTTP URL, then group by key pattern.
    const fileLinks   = [];   // all URL-valued properties, in order
    const designFiles = [];   // final-design-* (print-ready)
    const designPreviews = []; // design-* (preview images)

    Object.keys(p).forEach(function (key) {
      const val = (p[key] || '').trim();
      if (!val || !val.startsWith('http')) return;   // skip non-URL properties
      const k = key.toLowerCase();
      fileLinks.push({ label: key, url: val });
      if (k.startsWith('final-design'))        designFiles.push({ label: key, url: val });
      else if (k.startsWith('design'))         designPreviews.push({ label: key, url: val });
    });

    // Use design-1 (or first design preview) as the modal thumbnail
    const firstPreview = (designPreviews[0] || fileLinks[0] || {}).url || '';

    return {
      title:           item.product_title,
      // Customer-facing label with the internal "G+D " prefix stripped.
      // `title` stays the real product name so the team's form submission is unambiguous.
      displayTitle:    String(item.product_title || '').replace(/^G\+D\s+/i, ''),
      quantity:        item.quantity,
      line:            line,
      variantTitle:    item.variant_title,
      note:            p.customify_note || '',
      previewUrl:      firstPreview || item.image || '',  // modal thumbnail
      customifyPreview: firstPreview,                     // truthy only for Customify products
      fileLinks,       // every URL property — written to Google Form submission
      designFiles,     // final-design-* only (print-ready)
      designPreviews,  // design-* previews
      designId:        p.cucucover_id || '',
    };
  }

  function renderSelectionRow(s) {
    // Stepper floors at 1 — use the Remove (×) button to delete an item entirely
    const qtyRow = s.line
      ? `<div class="selection__qty-row">
           <button class="qty-btn" type="button" data-line="${s.line}" data-delta="-1" data-qty="${s.quantity}"${s.quantity <= 1 ? ' disabled' : ''} aria-label="Decrease quantity">−</button>
           <span class="qty-value">${s.quantity}</span>
           <button class="qty-btn" type="button" data-line="${s.line}" data-delta="1"  data-qty="${s.quantity}" aria-label="Increase quantity">+</button>
           <button class="qty-remove" type="button" data-line="${s.line}" aria-label="Remove design">✕</button>
         </div>`
      : `<div class="selection__meta">Qty: ${s.quantity}</div>`;

    return `
      <div class="selection" data-line="${s.line || ''}">
        ${s.previewUrl ? `<img class="selection__thumb" src="${escapeAttr(s.previewUrl)}" alt="">` : ''}
        <div class="selection__text">
          <div class="selection__title">${escapeText(s.displayTitle || s.title)}${s.variantTitle ? ' — ' + escapeText(s.variantTitle) : ''}</div>
          ${qtyRow}
          ${s.note ? `<div class="selection__meta">${escapeText(s.note)}</div>` : ''}
          ${s.customifyPreview ? `<div class="selection__preview-url"><a href="${escapeAttr(s.customifyPreview)}" target="_blank" rel="noopener">View design preview ↗</a></div>` : ''}
        </div>
      </div>
    `;
  }

  // ---- inline validation --------------------------------------------------

  function setFieldError(input, errorId, message) {
    const errorEl = document.getElementById(errorId);
    if (!errorEl) return;
    if (message) {
      input.setAttribute('aria-describedby', errorId);
      input.setAttribute('aria-invalid', 'true');
      input.classList.add('field--error');
      errorEl.textContent = message;
      errorEl.hidden = false;
    } else {
      input.removeAttribute('aria-describedby');
      input.removeAttribute('aria-invalid');
      input.classList.remove('field--error');
      errorEl.hidden = true;
    }
  }

  function clearFieldErrors() {
    ['name','company','rep','email'].forEach((name) => {
      const input = el.form?.elements[name];
      if (input) setFieldError(input, 'err-' + name, '');
    });
  }

  function validateForm() {
    let firstError = null;

    const checks = [
      { input: fi.name,    id: 'err-name',    test: (v) => v.trim().length > 0,          msg: 'Please enter your full name.' },
      { input: fi.company, id: 'err-company',  test: (v) => v.trim().length > 0,          msg: 'Please enter your bank name.' },
      { input: fi.rep,     id: 'err-rep',      test: (v) => v.trim().length > 0,          msg: `Please enter the name of your ${C.repLabel || 'sales representative'}.` },
      { input: fi.email,   id: 'err-email',    test: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), msg: 'Please enter a valid work email address.' },
    ];

    checks.forEach(({ input, id, test, msg }) => {
      if (!input) return;
      const val = input.value || '';
      const err = test(val) ? '' : msg;
      setFieldError(input, id, err);
      if (err && !firstError) firstError = input;
    });

    return firstError;
  }

  // Clear errors on input after first attempt
  ['name','company','rep','email'].forEach((name) => {
    el.form?.elements[name]?.addEventListener('input', function () {
      setFieldError(this, 'err-' + name, '');
    });
  });

  // ---- form submission ----------------------------------------------------

  el.form?.addEventListener('submit', async (e) => {
    e.preventDefault();

    clearFieldErrors();
    const firstInvalid = validateForm();
    if (firstInvalid) {
      firstInvalid.focus();
      return;
    }

    if (!FD.action) {
      console.error('[portal] form action not configured');
      return;
    }

    const submitBtn = el.form.querySelector('button[type="submit"]');
    submitBtn.disabled = true;
    submitBtn.textContent = 'Submitting…';

    try {
      await pollCart();
      const selections = lastCart.items.map((item, i) => formatLineItem(item, i + 1));
      const designsText = selections.map(formatDesignLine).join('\n\n');

      const fd = new FormData();
      fd.append(FD.name,    fi.name?.value    || '');
      fd.append(FD.company, fi.company?.value || '');
      fd.append(FD.rep,     fi.rep?.value     || '');
      fd.append(FD.email,   fi.email?.value   || '');
      fd.append(FD.designs, designsText);
      fd.append(FD.notes,   fi.notes?.value   || '');

      // Google Forms accepts form POSTs but blocks CORS reads — use no-cors.
      // The action URL is rendered server-side and read from a data attribute;
      // it is not present in the JavaScript source.
      await fetch(FD.action, {
        method: 'POST',
        mode:   'no-cors',
        body:   fd,
      });

      // Show confirmation
      el.doneSum.innerHTML = selections.map(renderSelectionRow).join('');
      showStep('done');
    } catch (err) {
      console.error('[portal] submit failed', err);
      showToast('Something went wrong — please try again.');
      submitBtn.disabled = false;
      submitBtn.textContent = 'Submit Selection';
    }
  });

  el.done?.addEventListener('click', async () => {
    try {
      await fetch('/cart/clear.js', { method: 'POST' });
    } catch (_) {}
    window.location.reload();
  });

  // ---- helpers ------------------------------------------------------------

  function formatDesignLine(s) {
    let line = `${s.title}${s.variantTitle ? ' — ' + s.variantTitle : ''} (qty ${s.quantity})`;
    if (s.designId)  line += `\n  Design ID: ${s.designId}`;
    if (s.note)      line += `\n  Note: ${s.note}`;
    // Write every Customify file link with its original property name as the label
    if (s.fileLinks && s.fileLinks.length) {
      s.fileLinks.forEach(function (f) {
        line += `\n  ${f.label}: ${f.url}`;
      });
    }
    return line;
  }

  function escapeText(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  function escapeAttr(s) {
    return escapeText(s);
  }

})();

// ── CUSTOMIZER FALLBACK ──────────────────────────────────────────────────────
// The Customify app serves the design customizer at /apps/customify/view/...
// When a product isn't configured/active in Customify (e.g. plan lapsed or the
// product was never set up on this store), the app responds with its own
// unbranded error page ("Opss...! Product not found"). That looks broken to
// clients. This block intercepts clicks on custom-design cards, probes the
// customizer first, and — if it's down — shows our own branded message
// pointing to support@customcardportal.com instead of letting Customify's
// error page render.
(function () {
  'use strict';

  var SUPPORT_EMAIL = 'support@customcardportal.com';
  // Matches Customify's "Opss...! Product not found. probably has been removed."
  // Kept specific so a *working* customizer page can never false-trigger this.
  var ERROR_RX = /product not found|opss/i;
  var cache = {};            // href -> 'ok' | 'down'  (per-session, avoids re-probing)
  var inFlight = {};         // href -> true while a probe is running

  function isCustomizerLink(a) {
    var href = a && a.getAttribute('href');
    return href && href.indexOf('/apps/customify/') !== -1 ? href : '';
  }

  // Probe the customizer URL same-origin; resolve 'ok' or 'down'.
  // Network errors resolve 'unknown' so we fail OPEN (never block a working
  // customizer because of a transient blip).
  function probe(href) {
    if (cache[href]) return Promise.resolve(cache[href]);
    return fetch(href, { credentials: 'same-origin' })
      .then(function (r) { return r.text().then(function (t) { return { ok: r.ok, t: t }; }); })
      .then(function (res) {
        cache[href] = (!res.ok || ERROR_RX.test(res.t)) ? 'down' : 'ok';
        return cache[href];
      })
      .catch(function () { return 'unknown'; });
  }

  // Branded, dismissible dialog shown when the customizer is unavailable.
  function showCustomizerDownDialog() {
    if (document.getElementById('ccp-cz-overlay')) return;
    var overlay = document.createElement('div');
    overlay.id = 'ccp-cz-overlay';
    overlay.className = 'ccp-cz-overlay';
    overlay.innerHTML =
      '<div class="ccp-cz-dialog" role="dialog" aria-modal="true" aria-labelledby="ccp-cz-title">' +
        '<button type="button" class="ccp-cz-close" aria-label="Close">&times;</button>' +
        '<h2 id="ccp-cz-title">Customizer temporarily unavailable</h2>' +
        '<p>The design customizer is being reactivated. Please contact ' +
          '<a href="mailto:' + SUPPORT_EMAIL + '">' + SUPPORT_EMAIL + '</a> ' +
          'to reactivate the customizer and we’ll get you back up and running.</p>' +
        '<button type="button" class="btn btn-primary ccp-cz-ok">Got it</button>' +
      '</div>';
    document.body.appendChild(overlay);

    function close() {
      overlay.remove();
      document.removeEventListener('keydown', onKey);
    }
    function onKey(e) { if (e.key === 'Escape') close(); }

    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    overlay.querySelector('.ccp-cz-close').addEventListener('click', close);
    overlay.querySelector('.ccp-cz-ok').addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    overlay.querySelector('.ccp-cz-ok').focus();
  }

  // Mark a card visually as unavailable (badge + dimmed) once a probe confirms down.
  function markCardDown(a) {
    var card = a.closest('.card');
    if (!card || card.classList.contains('card--cz-down')) return;
    card.classList.add('card--cz-down');
    var badge = document.createElement('span');
    badge.className = 'card__cz-badge';
    badge.textContent = 'Temporarily unavailable';
    var wrap = card.querySelector('.card__img')?.parentNode || card;
    wrap.appendChild(badge);
  }

  // Intercept clicks on customizer links.
  document.addEventListener('click', function (e) {
    var a = e.target.closest('.card__link');
    if (!a) return;
    var href = isCustomizerLink(a);
    if (!href) return;

    // Known good → allow normal navigation.
    if (cache[href] === 'ok') return;

    // Known down → block and show branded dialog.
    if (cache[href] === 'down') {
      e.preventDefault();
      showCustomizerDownDialog();
      return;
    }

    // Unknown → take over: probe, then navigate or show dialog.
    e.preventDefault();
    var card = a.closest('.card');
    if (card) card.classList.add('card--cz-checking');
    probe(href).then(function (state) {
      if (card) card.classList.remove('card--cz-checking');
      if (state === 'down') {
        markCardDown(a);
        showCustomizerDownDialog();
      } else {
        // 'ok' or 'unknown' (network blip) → let them through.
        window.location.assign(href);
      }
    });
  });

  // Background probe on load: pre-flag any down customizer cards so clients
  // see the state before clicking. Runs quietly; only marks DOWN on a definitive
  // error response (never on a network error), so transient blips don't false-flag.
  function probeAll() {
    var seen = {};
    document.querySelectorAll('.card__link').forEach(function (a) {
      var href = isCustomizerLink(a);
      if (!href || seen[href] || inFlight[href]) return;
      seen[href] = true;
      inFlight[href] = true;
      probe(href).then(function (state) {
        inFlight[href] = false;
        if (state === 'down') {
          document.querySelectorAll('.card__link').forEach(function (link) {
            if (isCustomizerLink(link) === href) markCardDown(link);
          });
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', probeAll);
  } else {
    probeAll();
  }
})();

// ── LIGHTBOX ─────────────────────────────────────────────────────────────────
(function () {
  'use strict';

  const lightbox = document.getElementById('portal-lightbox');
  const lbImg    = document.getElementById('lightbox-img');
  const lbClose  = document.getElementById('lightbox-close');
  if (!lightbox || !lbImg) return;

  function openLightbox(src, alt) {
    lbImg.src = '';
    lbImg.classList.add('loading');
    lbImg.alt = alt || '';
    lightbox.hidden = false;
    document.body.classList.add('lightbox-open');
    lbClose.focus();

    const tmp = new Image();
    tmp.onload = function () {
      lbImg.src = src;
      lbImg.classList.remove('loading');
    };
    tmp.onerror = function () {
      lbImg.src = src; // show anyway
      lbImg.classList.remove('loading');
    };
    tmp.src = src;
  }

  function closeLightbox() {
    lightbox.hidden = true;
    document.body.classList.remove('lightbox-open');
  }

  // Unified handler — works for both click (desktop) and touchend (mobile)
  function handleLightboxTrigger(e) {
    // Zoom button (⤢ overlay on premade cards)
    // NOTE: the button itself has no data-src-full; the src lives on the sibling <img>
    const zoomBtn = e.target.closest('.card__zoom-btn');
    if (zoomBtn) {
      e.preventDefault();
      e.stopPropagation();
      const card = zoomBtn.closest('.card');
      const img  = card ? card.querySelector('.card__img') : null;
      const src  = zoomBtn.dataset.srcFull || img?.dataset.srcFull || img?.src || '';
      const alt  = card ? (card.querySelector('.card__title')?.textContent || '') : '';
      if (src) openLightbox(src, alt);
      return;
    }

    // Direct tap/click on a premade card image
    const img = e.target.closest('img[data-lightbox]');
    if (img) {
      e.preventDefault();
      const src = img.dataset.srcFull || img.src || '';
      const alt = img.alt || '';
      if (src) openLightbox(src, alt);
      return;
    }
  }

  document.addEventListener('click',    handleLightboxTrigger);
  document.addEventListener('touchend', handleLightboxTrigger, { passive: false });

  // Close on button, backdrop click, or Escape
  lbClose.addEventListener('click', closeLightbox);
  lightbox.addEventListener('click', function (e) {
    if (e.target === lightbox) closeLightbox();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !lightbox.hidden) closeLightbox();
  });
})();
